/**
 * @file consent-texts-warnings.ts
 * @description Мягкие предупреждения полей текстов согласия (phase19.16,
 * Р3г; uiux-r3g-consent-texts §5). Публикацию не запрещают: ошибки — shared
 * `validateConsentTexts`, здесь только подсказки кабинета.
 *
 * Мягкие пределы — числа дизайнера (uiux §5), строже серверных: «кнопка ≤ 24»
 * (три в ряд на 360), «компактная — две строки на 360», «название ≤ 30»,
 * и смысловая проверка фразы «после согласия».
 */

import type { ConsentTextKind } from "@chaiberi/shared";

import { getTextAt } from "./consent-texts-form";

export type ConsentTextWarningCode = "consent_meaning" | "two_lines" | "button_long" | "label_long";

export const CONSENT_WARNING_TEXT: Readonly<Record<ConsentTextWarningCode, string>> = {
  consent_meaning: "Проверьте смысл: фраза должна говорить, что сервисы работают только с согласия",
  two_lines: "Больше двух строк на телефоне — полоса обрежет текст",
  button_long: "Длинная подпись: три кнопки в ряд на телефоне могут не поместиться",
  label_long: "Длинное название — в окне «Настроить» перенесётся на две строки",
};

/** Знаков в две строки компактной полосы на 360 (ширина 328, 12 px). */
const COMPACT_SOFT = 90;
const BUTTON_SOFT = 24;
const LABEL_SOFT = 30;

const length = (text: string): number => [...text].length;

/** Мягкое правило: поля → проверка. Реестр, а не каскад условий. */
const SOFT_RULES: ReadonlyArray<{ paths: readonly string[]; code: ConsentTextWarningCode; test: (text: string) => boolean }> = [
  { paths: ["consentPhrase"], code: "consent_meaning", test: (t) => t !== "" && !/согласи/iu.test(t) },
  { paths: ["compactConsent", "compactNotice"], code: "two_lines", test: (t) => length(t) > COMPACT_SOFT },
  {
    paths: ["buttons.acceptAll", "buttons.necessaryOnly", "buttons.configure", "buttons.ok", "buttons.refuse", "buttons.save"],
    code: "button_long",
    test: (t) => length(t) > BUTTON_SOFT,
  },
  {
    paths: ["necessary.label", "categories.analytics.label", "categories.ads.label"],
    code: "label_long",
    test: (t) => length(t) > LABEL_SOFT,
  },
];

/** Предупреждения по полям: путь → текст (у окна о риске их нет). */
export function consentFieldWarnings(kind: ConsentTextKind, texts: object): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  if (kind !== "BANNER") return map;
  for (const rule of SOFT_RULES) {
    for (const path of rule.paths) {
      if (!map.has(path) && rule.test(getTextAt(texts, path).trim())) map.set(path, CONSENT_WARNING_TEXT[rule.code]);
    }
  }
  return map;
}
